import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { OpenInNew as InternalLinkIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ---------------------------------------------------------------------------
// Chart parser & renderer (CSS-only bar charts)
// ---------------------------------------------------------------------------

interface ChartData {
  titulo: string;
  dados: Array<{ label: string; value: number }>;
}

function parseChartBlock(raw: string): ChartData | null {
  try {
    const lines = raw.trim().split('\n');
    let titulo = '';
    const dados: Array<{ label: string; value: number }> = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('titulo:')) {
        titulo = trimmed.replace('titulo:', '').trim();
      } else if (trimmed.startsWith('- ') && trimmed.includes(':')) {
        const withoutDash = trimmed.slice(2);
        const lastColon = withoutDash.lastIndexOf(':');
        if (lastColon > 0) {
          const label = withoutDash.slice(0, lastColon).trim();
          const value = parseFloat(withoutDash.slice(lastColon + 1).trim());
          if (!isNaN(value)) {
            dados.push({ label, value });
          }
        }
      }
    }

    if (dados.length === 0) return null;
    return { titulo, dados };
  } catch {
    return null;
  }
}

const ChatBarChart: React.FC<{ data: ChartData }> = ({ data }) => {
  const maxValue = Math.max(...data.dados.map(d => d.value), 1);

  const getBarColor = (value: number): string => {
    if (value > 75) return '#4caf50';
    if (value > 50) return '#2196f3';
    if (value > 25) return '#ff9800';
    return '#f44336';
  };

  return (
    <Box sx={{ my: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
      {data.titulo && (
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
          {data.titulo}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {data.dados.map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ minWidth: 90, maxWidth: 120, fontSize: '0.7rem', textAlign: 'right', flexShrink: 0, lineHeight: 1.2, wordBreak: 'break-word' }}
            >
              {item.label}
            </Typography>
            <Box sx={{ flex: 1, position: 'relative', height: 18, bgcolor: 'grey.200', borderRadius: 0.5, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.min((item.value / maxValue) * 100, 100)}%`,
                  bgcolor: getBarColor(item.value),
                  borderRadius: 0.5,
                  transition: 'width 0.6s ease-out',
                  minWidth: item.value > 0 ? 4 : 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: (item.value / maxValue) > 0.6 ? 'white' : 'text.primary',
                  lineHeight: 1,
                }}
              >
                {item.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Markdown Renderer (with internal links + chart code blocks)
// ---------------------------------------------------------------------------

const MarkdownContent: React.FC<{
  content: string;
  onNavigate?: (path: string) => void;
}> = ({ content, onNavigate }) => {
  const { processedContent, charts } = useMemo(() => {
    const chartMap: Record<string, ChartData> = {};
    let idx = 0;
    const processed = content.replace(/```chart\n([\s\S]*?)```/g, (_match, block) => {
      const parsed = parseChartBlock(block);
      if (parsed) {
        const key = `__CHART_${idx++}__`;
        chartMap[key] = parsed;
        return key;
      }
      return _match;
    });
    return { processedContent: processed, charts: chartMap };
  }, [content]);

  const segments = useMemo(() => {
    const chartKeys = Object.keys(charts);
    if (chartKeys.length === 0) return [{ type: 'md' as const, content: processedContent }];

    const result: Array<{ type: 'md' | 'chart'; content: string; chartKey?: string }> = [];
    let remaining = processedContent;

    for (const key of chartKeys) {
      const idx = remaining.indexOf(key);
      if (idx === -1) continue;
      const before = remaining.slice(0, idx);
      if (before.trim()) result.push({ type: 'md', content: before });
      result.push({ type: 'chart', content: '', chartKey: key });
      remaining = remaining.slice(idx + key.length);
    }
    if (remaining.trim()) result.push({ type: 'md', content: remaining });

    return result;
  }, [processedContent, charts]);

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'chart' && seg.chartKey && charts[seg.chartKey]) {
          return <ChatBarChart key={i} data={charts[seg.chartKey]} />;
        }

        return (
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <Typography variant="body2" component="p" sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
                  {children}
                </Typography>
              ),
              strong: ({ children }) => <strong>{children}</strong>,
              ul: ({ children }) => <Box component="ul" sx={{ pl: 2, mb: 0.5, mt: 0 }}>{children}</Box>,
              ol: ({ children }) => <Box component="ol" sx={{ pl: 2, mb: 0.5, mt: 0 }}>{children}</Box>,
              li: ({ children }) => (
                <Typography component="li" variant="body2" sx={{ mb: 0.25 }}>{children}</Typography>
              ),
              h3: ({ children }) => (
                <Typography variant="subtitle1" component="h3" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>
                  {children}
                </Typography>
              ),
              h4: ({ children }) => (
                <Typography variant="subtitle2" component="h4" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
                  {children}
                </Typography>
              ),
              a: ({ href, children }) => {
                if (href && href.startsWith('/')) {
                  return (
                    <MuiLink
                      component="button"
                      variant="body2"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        if (onNavigate) onNavigate(href);
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        textDecoration: 'none',
                        color: 'primary.main',
                        bgcolor: 'primary.50',
                        px: 0.5,
                        py: 0.125,
                        borderRadius: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                        verticalAlign: 'baseline',
                        border: '1px solid',
                        borderColor: 'primary.200',
                        '&:hover': {
                          bgcolor: 'primary.100',
                          textDecoration: 'none',
                        },
                      }}
                    >
                      {children}
                      <InternalLinkIcon sx={{ fontSize: '0.75rem', opacity: 0.7 }} />
                    </MuiLink>
                  );
                }
                return (
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                    {children}
                  </Typography>
                );
              },
              code: ({ className, children }) => {
                if (!className) {
                  return (
                    <Box component="code" sx={{ bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.85em' }}>
                      {children}
                    </Box>
                  );
                }
                return (
                  <Box component="pre" sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, overflow: 'auto', fontSize: '0.85em', mb: 1 }}>
                    <code>{children}</code>
                  </Box>
                );
              },
              table: ({ children }) => (
                <Box component="table" sx={{
                  width: '100%', borderCollapse: 'collapse', mb: 1, fontSize: '0.8em',
                  '& th, & td': { border: '1px solid', borderColor: 'grey.300', px: 0.75, py: 0.5, textAlign: 'left' },
                  '& th': { bgcolor: 'grey.100', fontWeight: 600 },
                }}>
                  {children}
                </Box>
              ),
            }}
          >
            {seg.content}
          </ReactMarkdown>
        );
      })}
    </>
  );
};

export default MarkdownContent;
